import { useState, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import UnlockIcon from '@wix/da-ds/pkg/Icons/24x24/Unlock';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import CommissionShopCard, {
  Props as CommissionShopCardProps,
} from '@wix/da-shared-react/pkg/ShopCard/CommissionShopCard';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  default as CommissionConfirmation,
  Props as CommissionConfirmationProps,
} from '@wix/da-shared-react/pkg/CommissionEditPopup/CommissionConfirmation';
import {
  EditDeviationPayload,
  DeleteDeviationPayload,
} from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { PapiDeviation } from '@wix/da-papi-types';

import { BiData, MoreMenuClickBiEvent } from '@wix/da-bi/pkg/events';
import s from './CommissionThumbWithMenu.scss';

const SHOP_CARD_WIDTH = 330;

export interface ActionProps {
  className?: string;
  isOwner: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
  editDeviation: (object: EditDeviationPayload) => void;
  toggleCommissionStatus: (object: PapiDeviation) => void;
  deleteDeviation: (object: DeleteDeviationPayload) => void;
}

type NonShopCardProps = CommissionShopCardProps & ActionProps;
export interface Props extends NonShopCardProps {}

function CommissionThumbWithMenu(props: Props) {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const [confirmationPopupType, setConfirmationPopupType] = useState<
    CommissionConfirmationProps['confirmationType']
  >(undefined);

  const {
    isOwner,
    deviation,
    className,
    isActive,
    allowedIndicators,
    isDeleted,
    toggleCommissionStatus,
    deleteDeviation,
    editDeviation,
  } = props;

  const openCommissionPauseConfirmation = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (isDeleted) {
        return;
      }
      setConfirmationPopupType(isActive ? 'disable' : 'enable');
    },
    [isActive, isDeleted]
  );

  const onChangeCommissionStatus = useCallback(() => {
    if (!deviation.commission) {
      return;
    }
    toggleCommissionStatus(deviation);
    setConfirmationPopupType(undefined);
  }, [deviation, toggleCommissionStatus]);

  const openCommissionDeleteConfirmation = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setConfirmationPopupType('remove');
    },
    []
  );

  const onDeleteCommission = useCallback(() => {
    deleteDeviation({ deviation });
    setConfirmationPopupType(undefined);
  }, [deviation, deleteDeviation]);

  const onCloseConfirmationModal = useCallback(() => {
    setConfirmationPopupType(undefined);
  }, []);

  const openCommissionEditModal = useCallback(
    (event: React.MouseEvent) => {
      if (!deviation.isTextEditable) {
        return;
      }
      event.preventDefault();
      editDeviation({ deviation });
    },
    [deviation, editDeviation]
  );

  const onDropdownOpen = useCallback(() => {
    logBiEvent(
      BiData<MoreMenuClickBiEvent>({
        evid: 417,
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
      })
    );
  }, [deviation, logBiEvent]);

  return (
    <div className={classnames(s['root'], className)}>
      <CommissionShopCard
        width={SHOP_CARD_WIDTH}
        deviation={deviation}
        withAuthorDetails={false}
        allowedIndicators={allowedIndicators}
        withDescriptionBlock={false}
      />
      {isOwner && (
        <div className={s['dropdown-wrapper']}>
          <IconButtonWithContextMenu
            icon={EllipsisIcon}
            size="medium"
            aria-label={t('common.more')}
            className={s['dropdown']}
            popperManagerProps={{
              onOpen: onDropdownOpen,
            }}
            items={[
              {
                items: [
                  deviation.isTextEditable && {
                    icon: <EditIcon />,
                    label: t('common.edit'),
                    onClick: openCommissionEditModal,
                  },
                  !deviation.isTextEditable && {
                    icon: <EditIcon />,
                    label: t('common.edit'),
                    title: t('common.commissions.legacyNotEditable'),
                    disabled: true,
                  },
                  {
                    icon: isActive ? <UnlockIcon /> : <LockIcon />,
                    label: isActive ? t('common.close') : t('common.open'),
                    onClick: openCommissionPauseConfirmation,
                  },
                ].filter(Boolean),
              },
              {
                items: [
                  {
                    icon: <TrashcanIcon />,
                    label: t('common.delete'),
                    onClick: openCommissionDeleteConfirmation,
                    danger: true,
                  },
                ],
              },
            ]}
            withBackground
          />
        </div>
      )}
      {confirmationPopupType && (
        <CommissionConfirmation
          isOpen
          confirmationType={confirmationPopupType}
          onCancel={onCloseConfirmationModal}
          onConfirm={
            confirmationPopupType === 'remove'
              ? onDeleteCommission
              : onChangeCommissionStatus
          }
        />
      )}
    </div>
  );
}

export default CommissionThumbWithMenu;
